<script setup>
import { computed, defineEmits, nextTick, onBeforeUnmount, ref, toRef, watch } from 'vue';
import { useDisclosure } from '@/js/core/composables/useDisclosure';
import {
  affiliationValues,
  allFilterSelection,
  filterOptionField,
  filterOptionId,
  flatSelectionValues,
  isSelectionAll,
  toggleAllSelection,
  toggleAffiliationFilterValue,
  toggleFlatFilterValue,
} from '@/js/core/utils/calendar-filter-state';

const props = defineProps({
  id: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: 'flat',
  },
  title: {
    type: String,
    default: '',
  },
  allLabel: {
    type: String,
    default: '',
  },
  options: {
    type: Array,
    default: () => [],
  },
  isOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['toggle']);

const selectedValues = defineModel({
  type: Object,
  default: () => allFilterSelection(),
});

const componentRef = ref(null);
const panelId = `filter-panel-${props.id}`;

const { triggerAttrs } = useDisclosure(componentRef, {
  isExpanded: toRef(props, 'isOpen'),
  panelId,
  onClose: () => emit('toggle'),
});

const toggleDropdown = () => {
  emit('toggle');
};

const dropdownEl = ref(null);
const positionTopPx = ref(null);
let rafId = null;

const measurePositionTop = () => {
  if (!dropdownEl.value) return;
  positionTopPx.value = `${Math.round(dropdownEl.value.getBoundingClientRect().top)}px`;
};

const scheduleMeasure = () => {
  if (rafId !== null) return;
  rafId = requestAnimationFrame(() => {
    rafId = null;
    measurePositionTop();
  });
};

const stopTracking = () => {
  window.removeEventListener('scroll', scheduleMeasure, { capture: true });
  window.removeEventListener('resize', scheduleMeasure);
  if (rafId !== null) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
};

watch(
  () => props.isOpen,
  async isOpen => {
    if (!isOpen) {
      stopTracking();
      positionTopPx.value = null;
      return;
    }
    await nextTick();
    measurePositionTop();
    window.addEventListener('scroll', scheduleMeasure, { passive: true, capture: true });
    window.addEventListener('resize', scheduleMeasure, { passive: true });
  }
);

onBeforeUnmount(stopTracking);

const selectOption = (value, children = []) => {
  if (value === 'all') {
    selectedValues.value = toggleAllSelection(selectedValues.value, props.id);
    return;
  }

  if (isAffiliationGroup.value) {
    selectedValues.value = toggleAffiliationFilterValue(selectedValues.value, value, children);
    return;
  }

  selectedValues.value = toggleFlatFilterValue(selectedValues.value, value);
};

const indicatorState = computed(() => {
  if (isSelectionAll(selectedValues.value)) return 'is-all';
  if (selectedCount.value === 0) return 'is-none';
  return 'is-partial';
});

const isAffiliationGroup = computed(
  () => props.type === 'affiliations' || props.id === 'affiliations'
);
const selectedCount = computed(() =>
  isAffiliationGroup.value
    ? Object.values(affiliationValues(selectedValues.value)).flat().length
    : flatSelectionValues(selectedValues.value).length
);

function isOptionSelected(option) {
  const id = filterOptionId(option);

  if (isAffiliationGroup.value) {
    const field = filterOptionField(option);

    return affiliationValues(selectedValues.value)[field]?.includes(id) === true;
  }

  return flatSelectionValues(selectedValues.value).includes(id);
}

function isOptionDisabled(option) {
  return option.disabled === true;
}
</script>

<template>
  <div ref="componentRef" class="calendar-filter-group">
    <button
      type="button"
      class="filter-group-label"
      :class="{ 'is-active': isOpen }"
      v-bind="triggerAttrs"
      @click.stop="toggleDropdown"
    >
      <div class="filter-indicator" :class="[`indicator--${id}`, indicatorState]"></div>
      {{ title }}
      <div class="filter-icon">
        <svg viewBox="0 0 501.62 501.62">
          <polygon
            points="250.81 500.5 500.81 0.5 0.81 0.5 250.81 500.5"
            vector-effect="non-scaling-stroke"
          ></polygon>
        </svg>
      </div>
    </button>

    <div
      v-if="isOpen"
      :id="panelId"
      ref="dropdownEl"
      class="filter-dropdown"
      :class="`group--${id}`"
      :style="positionTopPx ? { '--position-top': positionTopPx } : null"
    >
      <div class="filter-dropdown-inner">
        <label
          class="filter-option all-option"
          :class="{ 'is-selected': isSelectionAll(selectedValues) }"
        >
          <input
            type="checkbox"
            :checked="isSelectionAll(selectedValues)"
            @change="selectOption('all')"
          />
          <span>{{ allLabel }}</span>
        </label>

        <fieldset v-if="options.length" class="filter-options">
          <legend class="visually-hidden">{{ title }}</legend>
          <div v-for="(group, gIndex) in options" :key="gIndex" class="filter-group">
            <div v-if="group.sectionTitle" class="filter-section-header">
              {{ group.sectionTitle }}
            </div>

            <div v-for="option in group.choices || [group]" :key="option.id || option">
              <label
                v-if="typeof option === 'string'"
                class="filter-option"
                :class="{
                  'is-selected': isOptionSelected(option),
                  'is-disabled': isOptionDisabled(option),
                }"
              >
                <input
                  type="checkbox"
                  :checked="isOptionSelected(option)"
                  :disabled="isOptionDisabled(option)"
                  @change="!isOptionDisabled(option) && selectOption(option)"
                />
                <span :title="option">{{ option }}</span>
              </label>

              <label
                v-else-if="!option.children?.length"
                class="filter-option"
                :class="{
                  'is-selected': isOptionSelected(option),
                  'is-disabled': isOptionDisabled(option),
                }"
              >
                <input
                  type="checkbox"
                  :checked="isOptionSelected(option)"
                  :disabled="isOptionDisabled(option)"
                  @change="!isOptionDisabled(option) && selectOption(option)"
                />
                <span :title="option.fullLabel || option.label">{{ option.label }}</span>
              </label>

              <div v-else class="nested-group">
                <label
                  class="filter-option parent"
                  :class="{
                    'is-selected': isOptionSelected(option),
                    'is-disabled': isOptionDisabled(option),
                  }"
                >
                  <input
                    type="checkbox"
                    :checked="isOptionSelected(option)"
                    :disabled="isOptionDisabled(option)"
                    @change="!isOptionDisabled(option) && selectOption(option, option.children)"
                  />
                  <span :title="option.fullLabel || option.label">{{ option.label }}</span>
                </label>

                <ul v-if="option.children?.length" class="filter-children">
                  <li
                    v-for="child in option.children"
                    :key="child.id"
                    class="filter-option child"
                    :class="{
                      'is-selected': isOptionSelected(child),
                      'is-disabled': isOptionDisabled(child),
                    }"
                  >
                    <label>
                      <input
                        type="checkbox"
                        :checked="isOptionSelected(child)"
                        :disabled="isOptionDisabled(child)"
                        @change.stop="!isOptionDisabled(child) && selectOption(child)"
                      />
                      <span :title="child.fullLabel || child.label">{{ child.label }}</span>
                    </label>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </fieldset>
      </div>

      <button
        type="button"
        class="filter-dropdown-close"
        aria-label="Close filter"
        @click="emit('toggle')"
      >
        <svg viewBox="0 0 100 100" aria-hidden="true">
          <line x1="0" y1="0" x2="100" y2="100" stroke="black" stroke-width="10" />
          <line x1="100" y1="0" x2="0" y2="100" stroke="black" stroke-width="10" />
        </svg>
      </button>
    </div>
  </div>
</template>
